import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle, Gem, Sun, Heart, Compass, CakeSlice } from "lucide-react";
import { sound } from "@/lib/audio";

const CARDS = [
    {
        id: "abundance",
        numeral: "I",
        title: "Abundance",
        Icon: Gem,
        message:
            "May exciting opportunities find you, your hard work flourish, and prosperity arrive in beautiful and unexpected ways.",
    },
    {
        id: "joy",
        numeral: "II",
        title: "Joy",
        Icon: Sun,
        message:
            "May you have more reasons to laugh, more moments that take your breath away, and fewer things that deserve your stress.",
    },
    {
        id: "love",
        numeral: "III",
        title: "Love",
        Icon: Heart,
        message:
            "May you always be surrounded by people who appreciate you, support your dreams, and remind you how special you are.",
    },
    {
        id: "adventure",
        numeral: "IV",
        title: "Adventure",
        Icon: Compass,
        message:
            "May the coming year surprise you with experiences that become your favourite stories.",
    },
    {
        id: "cake",
        numeral: "V",
        title: "Cake",
        Icon: CakeSlice,
        message:
            "The universe has reviewed your request and unanimously approved unlimited birthday cake. No further documentation is required.",
    },
];

export default function Oracle({ reduce }) {
    const [flipped, setFlipped] = useState(() => new Set());
    const reportReady = flipped.size >= 3;

    const flip = (id) => {
        sound.chime(523.25 + flipped.size * 70);
        setFlipped((prev) => new Set(prev).add(id));
    };

    return (
        <section data-testid="oracle-section" className="relative z-[1] px-6 py-24 sm:py-32">
            <div className="mx-auto max-w-5xl">
                <motion.div
                    className="text-center"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    <p className="eyebrow-label mb-4">Act III — The Cosmic Oracle</p>
                    <h2 className="font-display text-3xl font-semibold leading-snug sm:text-5xl">
                        Mary, the universe has a <span className="gold-text italic">special message</span> for you
                    </h2>
                    <p className="mt-4 font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#C4B5FD]/70">
                        Choose any three cosmic gifts — tap a card to reveal it
                    </p>
                </motion.div>

                <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-5">
                    {CARDS.map((c, i) => {
                        const isFlipped = flipped.has(c.id);
                        return (
                            <motion.div
                                key={c.id}
                                className={i === 4 ? "col-span-2 mx-auto w-full max-w-[300px] lg:col-span-1 lg:max-w-none" : ""}
                                initial={{ opacity: 0, y: 60 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                            >
                            <button
                                data-testid={`oracle-card-${c.id}`}
                                aria-expanded={isFlipped}
                                aria-label={`Reveal cosmic gift: ${c.title}`}
                                onClick={() => flip(c.id)}
                                className="oracle-card h-64 w-full sm:h-72"
                                data-flipped={isFlipped}
                            >
                                <div className="oracle-inner">
                                    <div className="oracle-face oracle-front-face flex flex-col items-center justify-center gap-4 border border-[#F3D085]/30 bg-gradient-to-b from-[#1d1332] to-[#0f0a1e] p-4 transition-colors hover:border-[#F3D085]/60">
                                        <span className="font-mono2 text-[10px] tracking-[0.35em] text-[#F3D085]/60">
                                            {c.numeral}
                                        </span>
                                        <c.Icon className="h-8 w-8 text-[#F3D085]/80" strokeWidth={1.25} />
                                        <span className="font-display text-2xl font-semibold italic text-[#FBF7EE]">
                                            {c.title}
                                        </span>
                                        <Sparkle className="h-3.5 w-3.5 text-[#C4B5FD]/50" />
                                    </div>
                                    <div className="oracle-back-face flex flex-col items-center justify-center gap-3 border border-[#FFD700]/60 bg-gradient-to-b from-[#2A1438] to-[#17102b] p-5 text-center shadow-[0_0_45px_-8px_rgba(243,208,133,0.4)]">
                                        <span className="font-mono2 text-[9px] uppercase tracking-[0.3em] text-[#FFD700]">
                                            {c.title} — Granted
                                        </span>
                                        <p className="text-[13px] font-light leading-relaxed text-[#FBF7EE]/95 sm:text-sm">
                                            {c.message}
                                        </p>
                                    </div>
                                </div>
                            </button>
                            </motion.div>
                        );
                    })}
                </div>

                <AnimatePresence>
                    {reportReady && (
                        <motion.div
                            data-testid="cosmic-report"
                            className="mx-auto mt-14 max-w-xl rounded-2xl border border-[#C4B5FD]/30 bg-[#160E2A]/80 p-8 text-center backdrop-blur"
                            initial={{ opacity: 0, y: 30, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <p className="font-mono2 text-[11px] uppercase tracking-[0.32em] text-[#FFD700]">
                                Your cosmic report is ready
                            </p>
                            <p className="mt-4 font-display text-2xl font-light italic leading-relaxed text-[#FBF7EE]">
                                Predicted outlook: more laughter, meaningful connections, exciting
                                opportunities, and beautiful memories.
                            </p>
                            <p className="mt-4 text-xs font-light text-[#C4B5FD]/70">
                                Strictly playful — no actual supernatural predictions were made, and your
                                own sacred work remains the real magic.
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
}
